import { useState, useRef, useCallback, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { Upload, Download, Play, Pause, Image as ImageIcon, Film, Copy, RotateCcw, X, Eye, EyeOff, Trash2, AlertTriangle, Loader2 } from "lucide-react";

type MediaType = "image" | "video";

interface EllipseState {
  cx: number;
  cy: number;
  rx: number;
  ry: number;
  angle: number;
}

interface DotInfo {
  x: number;
  y: number;
  radius: number;
  r: number;
  g: number;
  b: number;
  a: number;
}

const DEFAULT_ELLIPSE: EllipseState = { cx: 0.5, cy: 0.5, rx: 0.3, ry: 0.3, angle: 0 };

const MAX_FILE_SIZE = 69 * 1024 * 1024; // 69 MB
const MAX_VIDEO_DURATION = 13; // seconds

/**
 * Software box blur (3-pass approximates Gaussian).
 * Works on all browsers including Safari/WebKit where ctx.filter is unsupported.
 */
function softwareBlur(ctx: CanvasRenderingContext2D, w: number, h: number, radius: number) {
  if (radius < 1) return;
  const r = Math.round(radius);
  const imgData = ctx.getImageData(0, 0, w, h);
  const pixels = imgData.data;
  const len = w * h * 4;
  const buf = new Uint8ClampedArray(len);

  // 3-pass box blur (horizontal then vertical, repeated)
  for (let pass = 0; pass < 3; pass++) {
    // Horizontal pass
    for (let y = 0; y < h; y++) {
      let ri = 0, gi = 0, bi = 0, ai = 0;
      const row = y * w;
      // Initialize window
      for (let x = -r; x <= r; x++) {
        const ix = Math.min(Math.max(x, 0), w - 1);
        const p = (row + ix) * 4;
        ri += pixels[p]; gi += pixels[p + 1]; bi += pixels[p + 2]; ai += pixels[p + 3];
      }
      const diam = r * 2 + 1;
      for (let x = 0; x < w; x++) {
        const p = (row + x) * 4;
        buf[p] = (ri / diam) | 0;
        buf[p + 1] = (gi / diam) | 0;
        buf[p + 2] = (bi / diam) | 0;
        buf[p + 3] = (ai / diam) | 0;
        // Slide window
        const addX = Math.min(x + r + 1, w - 1);
        const remX = Math.max(x - r, 0);
        const ap = (row + addX) * 4;
        const rp = (row + remX) * 4;
        ri += pixels[ap] - pixels[rp];
        gi += pixels[ap + 1] - pixels[rp + 1];
        bi += pixels[ap + 2] - pixels[rp + 2];
        ai += pixels[ap + 3] - pixels[rp + 3];
      }
    }
    // Copy buf back to pixels for vertical pass
    pixels.set(buf);

    // Vertical pass
    for (let x = 0; x < w; x++) {
      let ri = 0, gi = 0, bi = 0, ai = 0;
      for (let y = -r; y <= r; y++) {
        const iy = Math.min(Math.max(y, 0), h - 1);
        const p = (iy * w + x) * 4;
        ri += pixels[p]; gi += pixels[p + 1]; bi += pixels[p + 2]; ai += pixels[p + 3];
      }
      const diam = r * 2 + 1;
      for (let y = 0; y < h; y++) {
        const p = (y * w + x) * 4;
        buf[p] = (ri / diam) | 0;
        buf[p + 1] = (gi / diam) | 0;
        buf[p + 2] = (bi / diam) | 0;
        buf[p + 3] = (ai / diam) | 0;
        const addY = Math.min(y + r + 1, h - 1);
        const remY = Math.max(y - r, 0);
        const ap = (addY * w + x) * 4;
        const rp = (remY * w + x) * 4;
        ri += pixels[ap] - pixels[rp];
        gi += pixels[ap + 1] - pixels[rp + 1];
        bi += pixels[ap + 2] - pixels[rp + 2];
        ai += pixels[ap + 3] - pixels[rp + 3];
      }
    }
    pixels.set(buf);
  }
  ctx.putImageData(imgData, 0, 0);
}

/** Draw source to canvas then apply software blur */
function drawBlurred(source: HTMLImageElement | HTMLVideoElement | HTMLCanvasElement, canvas: HTMLCanvasElement, w: number, h: number, blurRadius: number) {
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(source, 0, 0, w, h);
  if (blurRadius > 0) {
    softwareBlur(ctx, w, h, blurRadius);
  }
}

/** Cross-browser save: uses Web Share API on iOS/mobile, falls back to <a download> */
async function saveCanvasImage(canvas: HTMLCanvasElement, fileName: string) {
  // Try Web Share API first (works in iOS webviews)
  if (navigator.share && navigator.canShare) {
    try {
      const blob = await new Promise<Blob>((resolve) =>
        canvas.toBlob((b) => resolve(b!), "image/png")
      );
      const file = new File([blob], fileName, { type: "image/png" });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file] });
        return;
      }
    } catch (e) {
      // User cancelled or not supported — fall through
      if ((e as DOMException)?.name === "AbortError") return;
    }
  }
  // Fallback: open blob URL in new tab (works better than <a download> in iframes)
  const blob = await new Promise<Blob>((resolve) =>
    canvas.toBlob((b) => resolve(b!), "image/png")
  );
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function toEllipseLocal(px: number, py: number, ox: number, oy: number, angle: number) {
  const cos = Math.cos(-angle);
  const sin = Math.sin(-angle);
  const dx = px - ox;
  const dy = py - oy;
  return { x: dx * cos - dy * sin, y: dx * sin + dy * cos };
}

function fromEllipseLocal(lx: number, ly: number, ox: number, oy: number, angle: number) {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return { x: ox + lx * cos - ly * sin, y: oy + lx * sin + ly * cos };
}

// Fullscreen info modal with canvas illustration for each parameter
type ParamType = "grid" | "resolution" | "blur" | "feather";

function drawParamIllustration(canvas: HTMLCanvasElement, type: ParamType) {
  const size = canvas.width;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, size, size);

  // Dark background
  ctx.fillStyle = "#18181b";
  ctx.fillRect(0, 0, size, size);

  if (type === "grid") {
    // Show 3 columns: sparse grid, medium, dense
    const configs = [
      { spacing: 40, label: "3" },
      { spacing: 24, label: "12" },
      { spacing: 12, label: "24" },
    ];
    const colW = size / 3;
    configs.forEach((cfg, ci) => {
      const ox = ci * colW;
      // subtle separator
      if (ci > 0) {
        ctx.strokeStyle = "rgba(255,255,255,0.08)";
        ctx.beginPath();
        ctx.moveTo(ox, 20);
        ctx.lineTo(ox, size - 30);
        ctx.stroke();
      }
      for (let y = cfg.spacing / 2 + 10; y < size - 30; y += cfg.spacing) {
        for (let x = cfg.spacing / 2; x < colW; x += cfg.spacing) {
          const r = cfg.spacing * 0.35;
          ctx.beginPath();
          ctx.arc(ox + x, y, Math.max(r, 1.5), 0, Math.PI * 2);
          ctx.fillStyle = "rgba(250,204,21,0.7)";
          ctx.fill();
        }
      }
      ctx.fillStyle = "rgba(255,255,255,0.5)";
      ctx.font = "12px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(cfg.label, ox + colW / 2, size - 10);
    });
  } else if (type === "resolution") {
    // Show 3 columns: small dots (0.3x), normal (1x), overlapping (2.5x)
    const configs = [
      { res: 0.3, label: "0.3×" },
      { res: 1.0, label: "1.0×" },
      { res: 2.5, label: "2.5×" },
    ];
    const colW = size / 3;
    const spacing = 28;
    configs.forEach((cfg, ci) => {
      const ox = ci * colW;
      if (ci > 0) {
        ctx.strokeStyle = "rgba(255,255,255,0.08)";
        ctx.beginPath();
        ctx.moveTo(ox, 20);
        ctx.lineTo(ox, size - 30);
        ctx.stroke();
      }
      for (let y = spacing / 2 + 10; y < size - 30; y += spacing) {
        for (let x = spacing / 2; x < colW; x += spacing) {
          const r = (spacing * 0.4) * cfg.res;
          ctx.beginPath();
          ctx.arc(ox + x, y, Math.max(r, 1), 0, Math.PI * 2);
          ctx.fillStyle = cfg.res > 1 ? "rgba(250,204,21,0.45)" : "rgba(250,204,21,0.7)";
          ctx.fill();
        }
      }
      ctx.fillStyle = "rgba(255,255,255,0.5)";
      ctx.font = "12px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(cfg.label, ox + colW / 2, size - 10);
    });
  } else if (type === "blur") {
    // Show 3 columns with rectangles: sharp, medium blur, heavy blur
    const configs = [
      { blur: 0, label: "0" },
      { blur: 8, label: "14" },
      { blur: 20, label: "30" },
    ];
    const colW = size / 3;
    const padding = 16;
    configs.forEach((cfg, ci) => {
      const ox = ci * colW + padding;
      const w = colW - padding * 2;
      const h = size - 70;
      // Draw a checkerboard then blur it
      const tmpCanvas = document.createElement("canvas");
      tmpCanvas.width = Math.round(w);
      tmpCanvas.height = Math.round(h);
      const tctx = tmpCanvas.getContext("2d")!;
      const sq = 16;
      for (let r = 0; r < h; r += sq) {
        for (let c = 0; c < w; c += sq) {
          tctx.fillStyle = (Math.floor(r / sq) + Math.floor(c / sq)) % 2 === 0 ? "#fbbf24" : "#1e1e22";
          tctx.fillRect(c, r, sq, sq);
        }
      }
      if (cfg.blur > 0) {
        softwareBlur(tctx, Math.round(w), Math.round(h), cfg.blur);
      }
      ctx.drawImage(tmpCanvas, ox, 20, w, h);

      ctx.fillStyle = "rgba(255,255,255,0.5)";
      ctx.font = "12px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(cfg.label, ci * colW + colW / 2, size - 10);
    });
  } else if (type === "feather") {
    // Show 3 columns: sharp edge ellipse, medium feather, heavy feather
    const configs = [
      { feather: 0, label: "0%" },
      { feather: 0.3, label: "30%" },
      { feather: 0.8, label: "80%" },
    ];
    const colW = size / 3;
    configs.forEach((cfg, ci) => {
      const cx = ci * colW + colW / 2;
      const cy = (size - 30) / 2 + 10;
      const rx = colW * 0.35;
      const ry = (size - 60) * 0.35;

      if (ci > 0) {
        ctx.strokeStyle = "rgba(255,255,255,0.08)";
        ctx.beginPath();
        ctx.moveTo(ci * colW, 20);
        ctx.lineTo(ci * colW, size - 30);
        ctx.stroke();
      }

      // Draw dots inside ellipse with feathering
      const spacing = 10;
      for (let y = 10; y < size - 30; y += spacing) {
        for (let x = ci * colW; x < (ci + 1) * colW; x += spacing) {
          const dx = (x - cx) / rx;
          const dy = (y - cy) / ry;
          const dist = Math.sqrt(dx * dx + dy * dy);
          let alpha = 0;
          if (cfg.feather < 0.01) {
            alpha = dist <= 1 ? 0.8 : 0;
          } else {
            if (dist <= 1 - cfg.feather) alpha = 0.8;
            else if (dist >= 1 + cfg.feather) alpha = 0;
            else alpha = 0.8 * (1 - (dist - (1 - cfg.feather)) / (2 * cfg.feather));
          }
          if (alpha < 0.02) continue;
          ctx.beginPath();
          ctx.arc(x, y, 3, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(250,204,21,${alpha})`;
          ctx.fill();
        }
      }

      ctx.fillStyle = "rgba(255,255,255,0.5)";
      ctx.font = "12px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(cfg.label, cx, size - 10);
    });
  }
}

function ParamInfoModal({ title, text, type }: { title: string; text: string; type: ParamType }) {
  const [open, setOpen] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (open && canvasRef.current) {
      drawParamIllustration(canvasRef.current, type);
    }
  }, [open, type]);

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="w-[15px] h-[15px] rounded-full border border-zinc-600 text-zinc-500 text-[9px] inline-flex items-center justify-center hover:border-zinc-400 hover:text-zinc-300 transition-colors select-none shrink-0"
      >
        i
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          {/* Double overlay for stronger coverage */}
          <div className="absolute inset-0 bg-black/70" />
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="relative bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-sm flex flex-col overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-zinc-800">
              <span className="text-zinc-100 text-base">{title}</span>
              <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors">
                <X className="w-4 h-4 text-zinc-400" />
              </button>
            </div>

            {/* Illustration */}
            <div className="p-4 flex justify-center">
              <canvas ref={canvasRef} width={300} height={300} className="w-full max-w-[300px] aspect-square rounded-xl" />
            </div>

            {/* Description */}
            <div className="px-4 pb-5">
              <p className="text-zinc-400 text-sm leading-relaxed">{text}</p>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export function ImageProcessor() {
  const [mediaType, setMediaType] = useState<MediaType | null>(null);
  const [mediaSrc, setMediaSrc] = useState<string | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [originalFileName, setOriginalFileName] = useState<string>("");
  const downloadCounter = useRef(0);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoTime, setVideoTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [copied, setCopied] = useState(false);

  // Upload progress & validation
  const [uploadPopup, setUploadPopup] = useState<{
    state: "loading" | "error";
    fileName: string;
    fileSize: number;
    progress: number;
    error?: string;
  } | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const displayCanvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const animFrameRef = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const pendingVideoRender = useRef(false);

  const [dotSize, setDotSize] = useState(8);
  const [resolution, setResolution] = useState(1.0);
  const [blurAmount, setBlurAmount] = useState(14);
  const [feather, setFeather] = useState(30);

  const [ellipse, setEllipse] = useState<EllipseState>({ ...DEFAULT_ELLIPSE });
  const [dragging, setDragging] = useState<null | "move" | "rx" | "ry" | "rotate">(null);
  const [showZone, setShowZone] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const dragStart = useRef({ mx: 0, my: 0, ellipse: { ...DEFAULT_ELLIPSE }, startAngle: 0, startDist: 0 });

  const [canvasW, setCanvasW] = useState(0);
  const [canvasH, setCanvasH] = useState(0);
  const defaultLoaded = useRef(false);

  // No default image preload — user uploads their own files (blob URLs are always same-origin)

  const calcMask = useCallback(
    (gx: number, gy: number, ecx: number, ecy: number, erx: number, ery: number, featherPx: number) => {
      const local = toEllipseLocal(gx, gy, ecx, ecy, ellipse.angle);
      const dx = local.x / erx;
      const dy = local.y / ery;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (featherPx < 1) return dist <= 1 ? 1 : 0;
      const featherNorm = featherPx / Math.max(erx, ery);
      if (dist <= 1 - featherNorm) return 1;
      if (dist >= 1 + featherNorm) return 0;
      return 1 - (dist - (1 - featherNorm)) / (2 * featherNorm);
    },
    [ellipse.angle]
  );

  const collectDots = useCallback(
    (source: HTMLImageElement | HTMLVideoElement, w: number, h: number): DotInfo[] => {
      const tmpCanvas = document.createElement("canvas");
      tmpCanvas.width = w;
      tmpCanvas.height = h;
      const tmpCtx = tmpCanvas.getContext("2d")!;
      tmpCtx.drawImage(source, 0, 0, w, h);
      const origData = tmpCtx.getImageData(0, 0, w, h);

      const gray = new Float32Array(w * h);
      for (let i = 0; i < w * h; i++) {
        const r = origData.data[i * 4];
        const g = origData.data[i * 4 + 1];
        const b = origData.data[i * 4 + 2];
        gray[i] = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      }

      const ecx = ellipse.cx * w;
      const ecy = ellipse.cy * h;
      const erx = ellipse.rx * w;
      const ery = ellipse.ry * h;
      const featherPx = (feather / 100) * Math.max(erx, ery);
      const spacing = dotSize * 2;
      const maxRadius = dotSize * 0.9 * resolution;
      const minRadius = dotSize * 0.05 * resolution;
      const dots: DotInfo[] = [];

      for (let gy2 = spacing / 2; gy2 < h; gy2 += spacing) {
        for (let gx2 = spacing / 2; gx2 < w; gx2 += spacing) {
          const ix = Math.min(Math.round(gx2), w - 1);
          const iy = Math.min(Math.round(gy2), h - 1);
          const mask = calcMask(gx2, gy2, ecx, ecy, erx, ery, featherPx);
          if (mask < 0.01) continue;

          const pi = (iy * w + ix) * 4;
          const r = origData.data[pi];
          const g = origData.data[pi + 1];
          const b = origData.data[pi + 2];
          const brightness = gray[iy * w + ix];
          const radius = (maxRadius - brightness * (maxRadius - minRadius)) * mask;
          if (radius < 0.2) continue;

          dots.push({ x: gx2, y: gy2, radius, r, g, b, a: mask });
        }
      }
      return dots;
    },
    [dotSize, resolution, ellipse, feather, calcMask]
  );

  const processFrame = useCallback(
    (source: HTMLImageElement | HTMLVideoElement, w: number, h: number, outputCanvas: HTMLCanvasElement) => {
      outputCanvas.width = w;
      outputCanvas.height = h;
      const ctx = outputCanvas.getContext("2d")!;

      // Software blur for Safari/WebKit compatibility
      ctx.drawImage(source, 0, 0, w, h);
      if (blurAmount > 0) {
        softwareBlur(ctx, w, h, blurAmount);
      }

      const origCanvas = document.createElement("canvas");
      origCanvas.width = w;
      origCanvas.height = h;
      const origCtx = origCanvas.getContext("2d")!;
      origCtx.drawImage(source, 0, 0, w, h);
      const origData = origCtx.getImageData(0, 0, w, h);

      const gray = new Float32Array(w * h);
      for (let i = 0; i < w * h; i++) {
        const r = origData.data[i * 4];
        const g = origData.data[i * 4 + 1];
        const b = origData.data[i * 4 + 2];
        gray[i] = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      }

      const ecx = ellipse.cx * w;
      const ecy = ellipse.cy * h;
      const erx = ellipse.rx * w;
      const ery = ellipse.ry * h;
      const featherPx = (feather / 100) * Math.max(erx, ery);
      const spacing = dotSize * 2;
      const maxRadius = dotSize * 0.9 * resolution;
      const minRadius = dotSize * 0.05 * resolution;

      for (let gy2 = spacing / 2; gy2 < h; gy2 += spacing) {
        for (let gx2 = spacing / 2; gx2 < w; gx2 += spacing) {
          const ix = Math.min(Math.round(gx2), w - 1);
          const iy = Math.min(Math.round(gy2), h - 1);
          const mask = calcMask(gx2, gy2, ecx, ecy, erx, ery, featherPx);
          if (mask < 0.01) continue;

          const pi = (iy * w + ix) * 4;
          const r = origData.data[pi];
          const g = origData.data[pi + 1];
          const b = origData.data[pi + 2];
          const brightness = gray[iy * w + ix];
          const radius = (maxRadius - brightness * (maxRadius - minRadius)) * mask;
          if (radius < 0.2) continue;

          ctx.beginPath();
          ctx.arc(gx2, gy2, radius, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${r},${g},${b},${mask})`;
          ctx.fill();
        }
      }
    },
    [dotSize, resolution, blurAmount, ellipse, feather, calcMask]
  );

  const processImage = useCallback(() => {
    const img = imgRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas) return;
    requestAnimationFrame(() => {
      const maxW = 800;
      const scale = img.width > maxW ? maxW / img.width : 1;
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      setCanvasW(w);
      setCanvasH(h);
      processFrame(img, w, h, canvas);
      setResultUrl(canvas.toDataURL("image/png"));
    });
  }, [processFrame]);

  useEffect(() => {
    if (mediaType === "image" && imgRef.current) processImage();
  }, [processImage, mediaType]);

  const videoLoop = useCallback(() => {
    const video = videoRef.current;
    const canvas = displayCanvasRef.current;
    if (!video || !canvas || video.paused || video.ended) return;
    const maxW = 640;
    const scale = video.videoWidth > maxW ? maxW / video.videoWidth : 1;
    const w = Math.round(video.videoWidth * scale);
    const h = Math.round(video.videoHeight * scale);
    setCanvasW(w);
    setCanvasH(h);
    processFrame(video, w, h, canvas);
    setVideoTime(video.currentTime);
    setVideoDuration(video.duration || 0);
    animFrameRef.current = requestAnimationFrame(videoLoop);
  }, [processFrame]);

  useEffect(() => {
    if (mediaType === "video" && videoPlaying) {
      animFrameRef.current = requestAnimationFrame(videoLoop);
    }
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [videoLoop, mediaType, videoPlaying]);

  useEffect(() => {
    if (mediaType === "video" && !videoPlaying && videoRef.current && displayCanvasRef.current) {
      const video = videoRef.current;
      if (video.readyState >= 2) {
        const maxW = 640;
        const scale = video.videoWidth > maxW ? maxW / video.videoWidth : 1;
        const w = Math.round(video.videoWidth * scale);
        const h = Math.round(video.videoHeight * scale);
        setCanvasW(w);
        setCanvasH(h);
        processFrame(video, w, h, displayCanvasRef.current);
        pendingVideoRender.current = false;
      }
    }
  }, [processFrame, mediaType, videoPlaying, canvasW, canvasH]);

  useLayoutEffect(() => {
    if (pendingVideoRender.current && displayCanvasRef.current && videoRef.current && canvasW > 0) {
      const video = videoRef.current;
      if (video.readyState >= 2) {
        processFrame(video, canvasW, canvasH, displayCanvasRef.current);
        pendingVideoRender.current = false;
      }
    }
  }, [canvasW, canvasH, processFrame]);

  const handleFile = (file: File) => {
    const isVideo = file.type.startsWith("video/");
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);

    // Size check
    if (file.size > MAX_FILE_SIZE) {
      setUploadPopup({
        state: "error",
        fileName: file.name,
        fileSize: file.size,
        progress: 0,
        error: `Файл занадто великий (${sizeMB} МБ). Максимум — ${MAX_FILE_SIZE / (1024 * 1024)} МБ.`,
      });
      return;
    }

    // Show loading popup
    setUploadPopup({
      state: "loading",
      fileName: file.name,
      fileSize: file.size,
      progress: 0,
    });

    // Read file via FileReader to track progress
    const reader = new FileReader();
    reader.onprogress = (e) => {
      if (e.lengthComputable) {
        setUploadPopup((prev) => prev ? { ...prev, progress: Math.round((e.loaded / e.total) * 100) } : prev);
      }
    };
    reader.onload = () => {
      setUploadPopup((prev) => prev ? { ...prev, progress: 100 } : prev);
      const src = URL.createObjectURL(file);

      // Store original file name (without extension) and reset download counter
      const baseName = file.name.replace(/\.[^.]+$/, "");
      setOriginalFileName(baseName);
      downloadCounter.current = 0;

      if (isVideo) {
        // Validate video duration before accepting
        const probeVideo = document.createElement("video");
        probeVideo.preload = "metadata";
        probeVideo.src = src;
        probeVideo.onloadedmetadata = () => {
          if (probeVideo.duration > MAX_VIDEO_DURATION) {
            URL.revokeObjectURL(src);
            setUploadPopup({
              state: "error",
              fileName: file.name,
              fileSize: file.size,
              progress: 0,
              error: `Відео занадто довге (${probeVideo.duration.toFixed(1)}с). Максимум — ${MAX_VIDEO_DURATION} секунд.`,
            });
            return;
          }

          // Video is valid — proceed
          setUploadPopup(null);
          setMediaSrc(src);
          setResultUrl(null);
          setVideoPlaying(false);
          setMediaType("video");
          imgRef.current = null;

          const video = document.createElement("video");
          video.src = src;
          video.muted = true;
          video.loop = true;
          video.playsInline = true;
          video.onloadeddata = () => {
            videoRef.current = video;
            setVideoDuration(video.duration || 0);
            setVideoTime(0);
            const maxW = 640;
            const scale = video.videoWidth > maxW ? maxW / video.videoWidth : 1;
            const w = Math.round(video.videoWidth * scale);
            const h = Math.round(video.videoHeight * scale);
            pendingVideoRender.current = true;
            setCanvasW(w);
            setCanvasH(h);
            video.play().then(() => {
              setVideoPlaying(true);
            }).catch(() => {});
          };
        };
        probeVideo.onerror = () => {
          URL.revokeObjectURL(src);
          setUploadPopup({
            state: "error",
            fileName: file.name,
            fileSize: file.size,
            progress: 0,
            error: "Не вдалося прочитати відеофайл. Перевірте формат.",
          });
        };
      } else {
        // Image — proceed
        setUploadPopup(null);
        setMediaSrc(src);
        setResultUrl(null);
        setVideoPlaying(false);
        setMediaType("image");
        videoRef.current = null;
        const img = new Image();
        img.onload = () => {
          imgRef.current = img;
          processImage();
        };
        img.src = src;
      }
    };
    reader.onerror = () => {
      setUploadPopup({
        state: "error",
        fileName: file.name,
        fileSize: file.size,
        progress: 0,
        error: "Помилка читання файлу.",
      });
    };
    reader.readAsArrayBuffer(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && (file.type.startsWith("image/") || file.type.startsWith("video/"))) handleFile(file);
  };

  const toggleVideo = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
      setVideoPlaying(true);
    } else {
      video.pause();
      setVideoPlaying(false);
    }
  };

  const resetEllipse = () => setEllipse({ ...DEFAULT_ELLIPSE });

  const resetToStart = () => {
    setMediaType(null);
    setMediaSrc(null);
    setResultUrl(null);
    setVideoPlaying(false);
    setCanvasW(0);
    setCanvasH(0);
    imgRef.current = null;
    videoRef.current = null;
    defaultLoaded.current = true; // prevent auto-load of default image
  };

  const copyToFigma = async () => {
    const source = mediaType === "image" ? imgRef.current : videoRef.current;
    if (!source || !canvasW || !canvasH) return;

    const dots = collectDots(source, canvasW, canvasH);

    const blurCanvas = document.createElement("canvas");
    blurCanvas.width = canvasW;
    blurCanvas.height = canvasH;
    const blurCtx = blurCanvas.getContext("2d")!;
    blurCtx.drawImage(source, 0, 0, canvasW, canvasH);
    if (blurAmount > 0) {
      softwareBlur(blurCtx, canvasW, canvasH, blurAmount);
    }
    const blurredDataUrl = blurCanvas.toDataURL("image/png");

    const srcCanvas = document.createElement("canvas");
    srcCanvas.width = canvasW;
    srcCanvas.height = canvasH;
    srcCanvas.getContext("2d")!.drawImage(source, 0, 0, canvasW, canvasH);
    const srcDataUrl = srcCanvas.toDataURL("image/png");

    const svgParts: string[] = [];
    svgParts.push(`<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${canvasW}" height="${canvasH}" viewBox="0 0 ${canvasW} ${canvasH}">`);
    svgParts.push(`  <image id="background" xlink:href="${blurredDataUrl}" width="${canvasW}" height="${canvasH}" />`);
    svgParts.push(`  <image id="background-source" xlink:href="${srcDataUrl}" width="${canvasW}" height="${canvasH}" opacity="0" />`);
    svgParts.push(`  <g id="dots">`);
    for (const dot of dots) {
      const hex = `#${dot.r.toString(16).padStart(2, "0")}${dot.g.toString(16).padStart(2, "0")}${dot.b.toString(16).padStart(2, "0")}`;
      svgParts.push(`    <circle cx="${dot.x.toFixed(1)}" cy="${dot.y.toFixed(1)}" r="${dot.radius.toFixed(2)}" fill="${hex}" opacity="${dot.a.toFixed(2)}" />`);
    }
    svgParts.push(`  </g>`);
    svgParts.push(`</svg>`);

    const svgString = svgParts.join("\n");

    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/plain": new Blob([svgString], { type: "text/plain" }),
        }),
      ]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = svgString;
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const getRelPos = (e: React.MouseEvent | React.Touch) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || !canvasW || !canvasH) return { x: 0, y: 0 };
    return {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    };
  };

  const handlePointerDown = (e: React.PointerEvent, type: "move" | "rx" | "ry" | "rotate") => {
    e.stopPropagation();
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    const pos = getRelPos(e);
    setDragging(type);
    const startAngle = Math.atan2(pos.y - ellipse.cy, pos.x - ellipse.cx);
    const startDist = Math.sqrt((pos.x - ellipse.cx) ** 2 + (pos.y - ellipse.cy) ** 2);
    dragStart.current = { mx: pos.x, my: pos.y, ellipse: { ...ellipse }, startAngle, startDist };
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!dragging) return;
    const pos = getRelPos(e);
    const dx = pos.x - dragStart.current.mx;
    const dy = pos.y - dragStart.current.my;
    const prev = dragStart.current.ellipse;

    if (dragging === "move") {
      setEllipse((p) => ({ ...p, cx: Math.max(0, Math.min(1, prev.cx + dx)), cy: Math.max(0, Math.min(1, prev.cy + dy)) }));
    } else if (dragging === "rx") {
      setEllipse((p) => ({ ...p, rx: Math.max(0.05, prev.rx + dx) }));
    } else if (dragging === "ry") {
      setEllipse((p) => ({ ...p, ry: Math.max(0.05, prev.ry + dy) }));
    } else if (dragging === "rotate") {
      const currentAngle = Math.atan2(pos.y - prev.cy, pos.x - prev.cx);
      const delta = currentAngle - dragStart.current.startAngle;
      const currentDist = Math.sqrt((pos.x - prev.cx) ** 2 + (pos.y - prev.cy) ** 2);
      const scaleFactor = dragStart.current.startDist > 0.001 ? currentDist / dragStart.current.startDist : 1;
      setEllipse((p) => ({
        ...p,
        angle: prev.angle + delta,
        rx: Math.max(0.05, prev.rx * scaleFactor),
        ry: Math.max(0.05, prev.ry * scaleFactor),
      }));
    }
  };

  const handlePointerUp = () => setDragging(null);

  const showCanvas = mediaSrc !== null;
  const isVideo = mediaType === "video";

  const ecxPx = ellipse.cx * canvasW;
  const ecyPx = ellipse.cy * canvasH;
  const erxPx = ellipse.rx * canvasW;
  const eryPx = ellipse.ry * canvasH;

  const rightHandle = fromEllipseLocal(erxPx, 0, ecxPx, ecyPx, ellipse.angle);
  const bottomHandle = fromEllipseLocal(0, eryPx, ecxPx, ecyPx, ellipse.angle);
  const cornerHandle = fromEllipseLocal(erxPx, eryPx, ecxPx, ecyPx, ellipse.angle);

  const angleDeg = (ellipse.angle * 180) / Math.PI;
  const resolutionLabel = resolution.toFixed(1) + "×";

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="min-h-full bg-zinc-950 text-zinc-100 flex flex-col items-center p-4 md:p-6 select-none">
      <canvas ref={canvasRef} style={{ display: "none" }} />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
      />

      {/* No media loaded — upload prompt */}
      {!mediaSrc && (
        <div className="flex flex-col items-center gap-6 mt-8">
          <h1 className="text-zinc-100">Dot Contour Generator</h1>
          <p className="text-zinc-400 max-w-lg text-center">
            Завантажте фото або відео — оберіть область еліпсом і отримайте крапковий ефект
          </p>
          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
            className="w-full max-w-lg h-64 border-2 border-dashed border-zinc-600 rounded-2xl flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-zinc-400 transition-colors"
          >
            <Upload className="w-10 h-10 text-zinc-500" />
            <span className="text-zinc-400">Натисніть або перетягніть фото / відео</span>
            <span className="text-zinc-600 text-xs flex items-center gap-3">
              <ImageIcon className="w-4 h-4" /> PNG, JPG <Film className="w-4 h-4 ml-2" /> MP4, WEBM
            </span>
            <span className="text-zinc-600 text-xs mt-1">Макс. 69 МБ, відео до 13 сек</span>
            <span className="text-zinc-700 text-[11px] mt-4">Created by Denys Shendryk on Figma Make</span>
          </div>
        </div>
      )}

      {/* Media loaded — two-column on desktop, stacked on mobile */}
      {mediaSrc && showCanvas && canvasW > 0 && (
        <div className="w-full max-w-[1400px] flex flex-col lg:flex-row gap-6 lg:gap-8 items-start">
          {/* LEFT PANEL: title, controls, action buttons */}
          <div className="w-full lg:w-[340px] lg:min-w-[300px] flex flex-col gap-5 lg:sticky lg:top-6 shrink-0">
            <div>
              <h1 className="text-zinc-100 text-[1.25rem]">Dot Contour Generator</h1>
              <p className="text-zinc-500 text-sm mt-1">
                Налаштуйте параметри ефекту та керуйте еліпсом на зображенні
              </p>
            </div>

            {/* Parameters */}
            <div className="flex flex-col gap-4 bg-zinc-900 rounded-xl p-4">
              {/* Grid */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-200 text-sm">Grid — <strong>{dotSize}</strong></span>
                  <ParamInfoModal title="Grid" text="Крок сітки (відстань між центрами крапок). Більше значення — рідша сітка з великими клітинками." type="grid" />
                </div>
                <input type="range" min={3} max={24} value={dotSize} onChange={(e) => setDotSize(Number(e.target.value))} className="accent-yellow-400" />
              </div>

              {/* Resolution */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-200 text-sm">Resolution — <strong>{resolutionLabel}</strong>{resolution > 1 && <span className="ml-2 text-xs text-yellow-400 opacity-80">перекриття</span>}</span>
                  <ParamInfoModal title="Resolution" text="Розмір крапок у комірці, незалежно від сітки. При 1× крапки займають комірку рівно. Більше 1× — крапки виходять за межі комірки і перекривають одна одну." type="resolution" />
                </div>
                <input type="range" min={0.1} max={3.0} step={0.05} value={resolution} onChange={(e) => setResolution(Number(e.target.value))} className="accent-yellow-400" />
              </div>

              {/* Blur */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-200 text-sm">Blur — <strong>{blurAmount}</strong></span>
                  <ParamInfoModal title="Blur" text="Ступінь розмиття фонового зображення під крапками. Більше значення — м'якший, більш абстрактний фон." type="blur" />
                </div>
                <input type="range" min={0} max={30} value={blurAmount} onChange={(e) => setBlurAmount(Number(e.target.value))} className="accent-yellow-400" />
              </div>

              {/* Feather */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-200 text-sm">Feather — <strong>{feather}%</strong></span>
                  <ParamInfoModal title="Feather" text="Плавність переходу ефекту на краях еліпсової зони. При 0% — різкий обрізаний край. При 80% — дуже плавне затухання крапок до країв." type="feather" />
                </div>
                <input type="range" min={0} max={80} value={feather} onChange={(e) => setFeather(Number(e.target.value))} className="accent-yellow-400" />
              </div>
            </div>

            {/* Action buttons — media controls */}
            <div className="flex gap-2">
                <button onClick={() => fileInputRef.current?.click()} className="flex-1 px-3 py-2 bg-zinc-800 text-zinc-400 rounded-lg hover:bg-zinc-700 transition-colors text-sm flex items-center justify-center gap-1.5">
                  <span className="relative w-4 h-4">
                    <ImageIcon className="w-4 h-4" />
                    <RotateCcw className="w-2.5 h-2.5 absolute -bottom-0.5 -right-0.5 text-zinc-300" />
                  </span>
                  Нове зображення або відео
                </button>
                <button onClick={() => setShowDeleteConfirm(true)} className="px-3 py-2 bg-zinc-800 text-zinc-400 rounded-lg hover:bg-zinc-700 hover:text-red-400 transition-colors flex items-center justify-center text-sm" title="Скинути зображення">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
                {showDeleteConfirm && createPortal(
                  <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(false)}>
                    <div className="absolute inset-0 bg-black/70" />
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
                    <div className="relative bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-sm flex flex-col overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                        <span className="text-zinc-100 text-base">Скинути зображення</span>
                        <button onClick={() => setShowDeleteConfirm(false)} className="w-8 h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors">
                          <X className="w-4 h-4 text-zinc-400" />
                        </button>
                      </div>
                      <div className="p-5 flex flex-col items-center gap-4">
                        <div className="w-14 h-14 rounded-full bg-red-500/15 flex items-center justify-center">
                          <Trash2 className="w-7 h-7 text-red-400" />
                        </div>
                        <p className="text-zinc-400 text-sm text-center leading-relaxed">
                          Ви дійсно хочете скинути поточне зображення та завантажити нове? Всі поточні налаштування буде втрачено.
                        </p>
                      </div>
                      <div className="flex gap-3 p-4 border-t border-zinc-800">
                        <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 px-4 py-2.5 bg-zinc-800 text-zinc-300 rounded-lg hover:bg-zinc-700 transition-colors text-sm">
                          Скасувати
                        </button>
                        <button onClick={() => { setShowDeleteConfirm(false); resetToStart(); }} className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-400 transition-colors text-sm">
                          Скинути
                        </button>
                      </div>
                    </div>
                  </div>,
                  document.body
                )}
            </div>

            {/* Zone controls */}
            <div className="flex gap-2">
                <button onClick={resetEllipse} className="flex-1 px-3 py-2 bg-zinc-800 text-zinc-400 rounded-lg hover:bg-zinc-700 transition-colors flex items-center justify-center gap-2 text-sm">
                  <RotateCcw className="w-3.5 h-3.5" />
                  Скинути зону
                </button>
                <button onClick={() => setShowZone((v) => !v)} className={`flex-1 px-3 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm ${showZone ? "bg-zinc-800 text-zinc-400 hover:bg-zinc-700" : "bg-zinc-700 text-zinc-200 hover:bg-zinc-600"}`}>
                  {showZone ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  {showZone ? "Приховати зону" : "Показати зону"}
                </button>
            </div>

            <span className="text-zinc-700 text-[11px]">Created by Denys Shendryk on Figma Make</span>
          </div>

          {/* RIGHT PANEL: canvas / image */}
          <div className="flex-1 min-w-0 flex flex-col items-center gap-4">
            <div
              ref={containerRef}
              className="relative inline-block"
              style={{ maxWidth: "100%", maxHeight: "85vh", touchAction: "none" }}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
            >
              {isVideo ? (
                <canvas
                  ref={displayCanvasRef}
                  width={canvasW}
                  height={canvasH}
                  className="max-w-full max-h-[85vh] rounded-xl shadow-2xl block"
                  style={{ aspectRatio: `${canvasW}/${canvasH}` }}
                />
              ) : (
                resultUrl && (
                  <img src={resultUrl} alt="Result" className="max-w-full max-h-[85vh] rounded-xl shadow-2xl block" draggable={false} />
                )
              )}

              {showZone && (
              <svg
                className="absolute inset-0 w-full h-full pointer-events-none"
                viewBox={`0 0 ${canvasW} ${canvasH}`}
                preserveAspectRatio="xMidYMid meet"
              >
                <ellipse
                  cx={ecxPx} cy={ecyPx} rx={erxPx} ry={eryPx}
                  fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" strokeDasharray="6 4"
                  transform={`rotate(${angleDeg} ${ecxPx} ${ecyPx})`}
                />
                {/* Move handle — center */}
                <circle cx={ecxPx} cy={ecyPx} r="32" fill="transparent" className="pointer-events-auto cursor-move" onPointerDown={(e) => handlePointerDown(e, "move")} />
                <circle cx={ecxPx} cy={ecyPx} r="16" fill="rgba(250,204,21,0.75)" stroke="white" strokeWidth="1.5" className="pointer-events-auto cursor-move" onPointerDown={(e) => handlePointerDown(e, "move")} />
                {/* RX handle — right */}
                <circle cx={rightHandle.x} cy={rightHandle.y} r="28" fill="transparent" className="pointer-events-auto cursor-ew-resize" onPointerDown={(e) => handlePointerDown(e, "rx")} />
                <circle cx={rightHandle.x} cy={rightHandle.y} r="14" fill="rgba(160,160,160,0.55)" stroke="rgba(255,255,255,0.45)" strokeWidth="1" className="pointer-events-auto cursor-ew-resize" onPointerDown={(e) => handlePointerDown(e, "rx")} />
                <text x={rightHandle.x} y={rightHandle.y} textAnchor="middle" dominantBaseline="middle" fontSize="14" fill="rgba(255,255,255,0.85)" className="pointer-events-auto cursor-ew-resize" style={{ userSelect: "none" }} onPointerDown={(e) => handlePointerDown(e, "rx")} transform={`rotate(${angleDeg} ${rightHandle.x} ${rightHandle.y})`}>↔</text>
                {/* RY handle — bottom */}
                <circle cx={bottomHandle.x} cy={bottomHandle.y} r="28" fill="transparent" className="pointer-events-auto cursor-ns-resize" onPointerDown={(e) => handlePointerDown(e, "ry")} />
                <circle cx={bottomHandle.x} cy={bottomHandle.y} r="14" fill="rgba(160,160,160,0.55)" stroke="rgba(255,255,255,0.45)" strokeWidth="1" className="pointer-events-auto cursor-ns-resize" onPointerDown={(e) => handlePointerDown(e, "ry")} />
                <text x={bottomHandle.x} y={bottomHandle.y} textAnchor="middle" dominantBaseline="middle" fontSize="14" fill="rgba(255,255,255,0.85)" className="pointer-events-auto cursor-ns-resize" style={{ userSelect: "none" }} onPointerDown={(e) => handlePointerDown(e, "ry")} transform={`rotate(${angleDeg} ${bottomHandle.x} ${bottomHandle.y})`}>↕</text>
                {/* Rotate + scale handle — corner */}
                <circle cx={cornerHandle.x} cy={cornerHandle.y} r="36" fill="transparent" className="pointer-events-auto cursor-grab" onPointerDown={(e) => handlePointerDown(e, "rotate")} />
                <circle cx={cornerHandle.x} cy={cornerHandle.y} r="24" fill="rgba(250,204,21,0.75)" stroke="white" strokeWidth="1.5" className="pointer-events-auto cursor-grab" onPointerDown={(e) => handlePointerDown(e, "rotate")} />
                <text x={cornerHandle.x} y={cornerHandle.y + 1} textAnchor="middle" dominantBaseline="middle" fontSize="16" fill="rgba(0,0,0,0.7)" className="pointer-events-auto cursor-grab" style={{ userSelect: "none" }} onPointerDown={(e) => handlePointerDown(e, "rotate")}>↻</text>
              </svg>
              )}
            </div>

            {/* Export buttons — under preview */}
            <div className="flex flex-col md:flex-row md:flex-wrap gap-2 w-full">
              {/* Video timeline player */}
              {isVideo && videoRef.current && (
                <div className="flex items-center gap-3 bg-zinc-900 rounded-xl px-3 py-2.5 w-full">
                  <button onClick={toggleVideo} className="w-8 h-8 rounded-full bg-yellow-500 hover:bg-yellow-400 text-zinc-900 flex items-center justify-center shrink-0 transition-colors">
                    {videoPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
                  </button>
                  <span className="text-zinc-500 text-xs tabular-nums shrink-0 w-[34px] text-right">
                    {formatTime(videoTime)}
                  </span>
                  <input
                    type="range"
                    min={0}
                    max={videoDuration || 1}
                    step={0.01}
                    value={videoTime}
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      const t = Number(e.target.value);
                      if (videoRef.current) {
                        videoRef.current.currentTime = t;
                        setVideoTime(t);
                        // Render frame immediately when scrubbing while paused
                        if (videoRef.current.paused && displayCanvasRef.current) {
                          const video = videoRef.current;
                          const render = () => {
                            if (video.readyState >= 2 && displayCanvasRef.current) {
                              const maxW = 640;
                              const scale = video.videoWidth > maxW ? maxW / video.videoWidth : 1;
                              const w = Math.round(video.videoWidth * scale);
                              const h = Math.round(video.videoHeight * scale);
                              processFrame(video, w, h, displayCanvasRef.current);
                            }
                          };
                          video.onseeked = () => { render(); video.onseeked = null; };
                        }
                      }
                    }}
                    onMouseUp={() => {
                      if (videoRef.current && videoPlaying) {
                        videoRef.current.play();
                      }
                    }}
                    className="flex-1 accent-yellow-400 h-1.5 cursor-pointer"
                  />
                  <span className="text-zinc-500 text-xs tabular-nums shrink-0 w-[34px]">
                    {formatTime(videoDuration)}
                  </span>
                </div>
              )}

              {/* Export buttons */}
              <div className="flex flex-col md:flex-row gap-2 w-full">
                {mediaType === "image" && resultUrl && (
                  <button
                    onClick={async () => {
                      const canvas = canvasRef.current;
                      if (!canvas) return;
                      downloadCounter.current++;
                      const fileName = `${originalFileName}-ds-${downloadCounter.current}.png`;
                      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                      if (isMobile && navigator.share && navigator.canShare) {
                        try {
                          const blob = await new Promise<Blob>((res) => canvas.toBlob((b) => res(b!), "image/png"));
                          const file = new File([blob], fileName, { type: "image/png" });
                          if (navigator.canShare({ files: [file] })) {
                            await navigator.share({ files: [file] });
                            return;
                          }
                        } catch (e) {
                          if ((e as DOMException)?.name === "AbortError") return;
                        }
                      }
                      const blob = await new Promise<Blob>((res) => canvas.toBlob((b) => res(b!), "image/png"));
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement("a");
                      link.href = url;
                      link.download = fileName;
                      link.click();
                      setTimeout(() => URL.revokeObjectURL(url), 5000);
                    }}
                    className="w-full md:w-auto md:flex-1 md:min-w-0 flex items-center justify-center gap-2 px-4 py-2.5 bg-yellow-500 text-zinc-900 rounded-lg hover:bg-yellow-400 transition-colors whitespace-nowrap"
                  >
                    <Download className="w-4 h-4" />
                    Завантажити PNG
                  </button>
                )}

                {mediaType === "video" && (
                  <button
                    onClick={async () => {
                      const canvas = displayCanvasRef.current;
                      if (!canvas) return;
                      downloadCounter.current++;
                      const fileName = `${originalFileName}-ds-${downloadCounter.current}.png`;
                      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                      if (isMobile && navigator.share && navigator.canShare) {
                        try {
                          const blob = await new Promise<Blob>((res) => canvas.toBlob((b) => res(b!), "image/png"));
                          const file = new File([blob], fileName, { type: "image/png" });
                          if (navigator.canShare({ files: [file] })) {
                            await navigator.share({ files: [file] });
                            return;
                          }
                        } catch (e) {
                          if ((e as DOMException)?.name === "AbortError") return;
                        }
                      }
                      const blob = await new Promise<Blob>((res) => canvas.toBlob((b) => res(b!), "image/png"));
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement("a");
                      link.href = url;
                      link.download = fileName;
                      link.click();
                      setTimeout(() => URL.revokeObjectURL(url), 5000);
                    }}
                    className="w-full md:w-auto md:flex-1 md:min-w-0 flex items-center justify-center gap-2 px-4 py-2.5 bg-yellow-500 text-zinc-900 rounded-lg hover:bg-yellow-400 transition-colors whitespace-nowrap"
                  >
                    <ImageIcon className="w-4 h-4" />
                    Зберегти кадр
                  </button>
                )}

                <button onClick={copyToFigma} className="hidden md:flex md:flex-1 md:min-w-0 items-center justify-center gap-2 px-4 py-2.5 bg-violet-600 text-white rounded-lg hover:bg-violet-500 transition-colors whitespace-nowrap">
                  <Copy className="w-4 h-4" />
                  {copied ? "Скопійовано!" : "Копіювати для Figma"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload progress / error popup */}
      {uploadPopup && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" />
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="relative bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-sm flex flex-col overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-zinc-800">
              <span className="text-zinc-100 text-base">
                {uploadPopup.state === "loading" ? "Завантаження файлу" : "Помилка"}
              </span>
              {uploadPopup.state === "error" && (
                <button onClick={() => setUploadPopup(null)} className="w-8 h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors">
                  <X className="w-4 h-4 text-zinc-400" />
                </button>
              )}
            </div>
            <div className="p-5 flex flex-col items-center gap-4">
              {uploadPopup.state === "loading" ? (
                <>
                  <div className="w-14 h-14 rounded-full bg-yellow-500/15 flex items-center justify-center">
                    <Loader2 className="w-7 h-7 text-yellow-400 animate-spin" />
                  </div>
                  <div className="w-full flex flex-col gap-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-300 truncate max-w-[200px]">{uploadPopup.fileName}</span>
                      <span className="text-zinc-500 shrink-0">{uploadPopup.progress}%</span>
                    </div>
                    <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-yellow-400 rounded-full transition-all duration-200"
                        style={{ width: `${uploadPopup.progress}%` }}
                      />
                    </div>
                    <span className="text-zinc-600 text-xs">
                      {(uploadPopup.fileSize / (1024 * 1024)).toFixed(1)} МБ
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-14 h-14 rounded-full bg-red-500/15 flex items-center justify-center">
                    <AlertTriangle className="w-7 h-7 text-red-400" />
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-zinc-300 text-sm truncate max-w-[260px]">{uploadPopup.fileName}</span>
                    <p className="text-red-400 text-sm text-center leading-relaxed">{uploadPopup.error}</p>
                  </div>
                </>
              )}
            </div>
            {uploadPopup.state === "error" && (
              <div className="p-4 border-t border-zinc-800">
                <button onClick={() => setUploadPopup(null)} className="w-full px-4 py-2.5 bg-zinc-800 text-zinc-300 rounded-lg hover:bg-zinc-700 transition-colors text-sm">
                  Закрити
                </button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}